/**
 * injectAssetVars
 *
 * Injects image CSS custom properties as a low-priority <style> element so
 * tenant.css can override them naturally through the CSS cascade.
 *
 * Strategy:
 *   The fallback URLs (BASE_URL-prefixed local paths) are written into a
 *   <style id="asset-fallbacks"> inserted as the FIRST child of <head>.
 *   injectTenantCss() then moves the tenant.css <link> to the END of <head>,
 *   so the cascade order is:
 *
 *     1. #asset-fallbacks <style>  (lowest — our BASE_URL fallback)
 *     2. Vite-bundled styles       (includes tenant.module.css defaults)
 *     3. tenant.css <link>         (highest — tenant CDN values win)
 *
 *   Because we use a stylesheet rule (not an inline style), tenant.css
 *   overrides the image variables through the normal CSS cascade without
 *   any programmatic fetch or CORS dependency.
 */
import { getFallbackUrl } from "./asset";

const IMAGE_ASSETS: Record<string, string> = {
  "--image-banner-1":        "asset/banner-1.png",
  "--image-banner-2":        "asset/banner-2.png",
  "--image-custom-icons-sample-icon-svg": "asset/custom-icons/sample-icon.svg",
  "--image-fav":             "asset/fav.png",
  "--image-favicon":         "asset/logo.ico",
  "--image-flag-en":         "asset/flags/gb.png",
  "--image-flag-es":         "asset/flags/es.png",
  "--image-impactomics-favicon-2-svg": "asset/Impactomics_Favicon-2.svg",
  "--image-impactomics-favicon-radious-svg": "asset/Impactomics_Favicon_Radious.svg",
  "--image-logo":            "asset/logo.png",
  "--image-logo-svg":        "asset/logo.svg",
};

export function injectAssetVars(): void {
  if (typeof document === "undefined") return;

  // Idempotent — skip if already injected (e.g. Vite HMR)
  if (document.getElementById("asset-fallbacks")) return;

  const rules = Object.entries(IMAGE_ASSETS)
    .map(([varName, assetPath]) => `  ${varName}: url("${getFallbackUrl(assetPath)}");`)
    .join("\n");

  const style = document.createElement("style");
  style.id          = "asset-fallbacks";
  style.textContent = `:root {\n${rules}\n}`;

  // Insert as the FIRST element of <head> so it has the lowest cascade priority.
  // Any stylesheet added later (including tenant.css moved to end by injectTenantCss)
  // will override these values without needing inline-style hacks.
  document.head.insertBefore(style, document.head.firstChild);
}
